import { useState } from 'react';
import axios from 'axios';
import { ChevronRightIcon, ChevronLeftIcon, ChevronDownIcon, FileTextIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { BuildingCityTownIcon, ChangeSwapArrowIcon, ReceiptIcon } from '@qitech/icons';

import { ErrorPage } from '../../../ErrorPage';
import { SocialSecurityLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { SocialSecurityDocumentViewer } from '../components/DocumentViewer';
import * as S from './styles';

interface FolderMode {
    onBack: () => void;
    currentDecision?: string;
    submitDecision: (decision: 'accepted' | 'rejected') => Promise<void>;
    refreshDetail?: () => Promise<any>;
}

interface PortabilityAndRefinancingSummaryProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
    folderMode?: FolderMode;
}

const formatDate = (inputDate: string): string => {
    const [year, month, day] = inputDate.split('-');
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const formatPercent = (value: number): string =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

export const SocialSecurityPortabilityAndRefinancingSummary = ({
    signData,
    setSignerStep,
    setSignData,
    folderMode,
}: PortabilityAndRefinancingSummaryProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);
    const initialError = { has_error: false, status: 0 };

    const isFolderMode = !!folderMode;
    const folderDecided = folderMode?.currentDecision;
    const folderAccepted = folderDecided === 'accepted';
    const folderRejected = folderDecided === 'declined' || folderDecided === 'rejected';

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(false);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [showCheckboxModal, setShowCheckboxModal] = useState(false);
    const [viewerDoc, setViewerDoc] = useState<{
        url: string | null;
        title: string;
        extract?: (data: any) => string | null | undefined;
    } | null>(null);
    const [portabilityOpen, setPortabilityOpen] = useState(false);
    const [refinancingOpen, setRefinancingOpen] = useState(false);

    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([]);

    const refinancing = signData?.refinancing_operation;
    const hasRefinancing = refinancing != null;
    const refinOp = refinancing?.credit_operation ?? {};
    const creditOperation = signData?.credit_operation_data ?? {};
    const documentUrl = signData?.document_url;
    const hasDocumentUrlKey = !!signData && 'document_url' in signData;
    const refinDocumentUrl = signData?.refinancing_document_url;
    const hasRefinDocumentUrlKey = !!signData && 'refinancing_document_url' in signData;

    const pollForDocumentUrl = async (
        extract: (data: any) => string | null | undefined,
    ): Promise<string | null | undefined> => {
        if (isFolderMode) {
            if (!folderMode?.refreshDetail) return extract(signData);
            const fresh = await folderMode.refreshDetail();
            if (!fresh) return extract(signData);
            setSignData({ ...signData, ...fresh });
            return extract(fresh);
        }
        const config = { headers: { Authorization: signData.signer_token } };
        const r = await axios.get('/signer', config);
        setSignData({ ...signData, ...r.data });
        return extract(r.data);
    };

    const handleFolderDecision = async (decision: 'accepted' | 'rejected') => {
        if (loading || !folderMode) return;
        setLoading(true);
        try {
            await folderMode.submitDecision(decision);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (hasMadeRequest) return;

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload = {
            step: 'portability_and_refinancing_summary',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((error) => {
                const status = [401, 403, 409].includes(error.response?.status)
                    ? error.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleButtonClick = () => {
        if (!hasScrolledToEnd) {
            scrollToBottom();
            return;
        }
        if (!isChecked) {
            setShowCheckboxModal(true);
            return;
        }
        handleSubmit();
    };

    const renderContractCallout = (
        hasKey: boolean,
        url: string | null | undefined,
        title: string,
        linkText: string,
        extract: (data: any) => string | null | undefined,
    ) => {
        if (!hasKey) return null;
        return (
            <S.InfoCallout $color={primary}>
                <S.IconWrapper $color={primary}>
                    <FileTextIcon width={22} height={22} />
                </S.IconWrapper>
                <S.InfoText $color={primary}>
                    Para mais informações,{' '}
                    <a
                        href={url || '#'}
                        onClick={(e) => {
                            e.preventDefault();
                            setViewerDoc({ url: url ?? null, title, extract });
                        }}
                    >
                        {linkText}
                    </a>
                </S.InfoText>
            </S.InfoCallout>
        );
    };

    const renderPortabilityContractCallout = () =>
        renderContractCallout(
            hasDocumentUrlKey,
            documentUrl,
            'Contrato da operação de portabilidade',
            'consulte o contrato da operação de portabilidade.',
            (d) => d?.document_url,
        );

    const renderRefinancingContractCallout = () =>
        renderContractCallout(
            hasRefinDocumentUrlKey,
            refinDocumentUrl,
            'Contrato da operação de refinanciamento',
            'consulte o contrato da operação de refinanciamento.',
            (d) => d?.refinancing_document_url,
        );

    const renderConsentCheckbox = () => (
        <S.CheckboxWrapper>
            <CheckboxField
                id="portability-refinancing-terms"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                checkboxColor={primary}
                textColor="#2d3748"
            >
                <strong style={{ color: primary }}>Confirmo que li e estou de acordo</strong>
                {hasRefinancing
                    ? ' com as condições de contratação dos meus contratos.'
                    : ' com as condições de contratação do meu empréstimo.'}
            </CheckboxField>
        </S.CheckboxWrapper>
    );

    if (loading) return <SocialSecurityLoading />;

    if (viewerDoc) {
        return (
            <SocialSecurityDocumentViewer
                url={viewerDoc.url}
                title={viewerDoc.title}
                onBack={() => setViewerDoc(null)}
                pollForUrl={viewerDoc.extract ? () => pollForDocumentUrl(viewerDoc.extract!) : undefined}
            />
        );
    }

    const renderShell = (content: JSX.Element, buttonLabel: string) => (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            {isFolderMode && (
                <S.FolderBackBar>
                    <S.FolderBackButton $color={primary} type="button" onClick={folderMode!.onBack}>
                        <ChevronLeftIcon width={18} height={18} />
                        Voltar para propostas
                    </S.FolderBackButton>
                </S.FolderBackBar>
            )}

            <S.ScrollContent>
                {content}
                {!isFolderMode && renderConsentCheckbox()}
                <div ref={contentEndRef} />
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.Footer>
                {isFolderMode ? (
                    <>
                        {(folderAccepted || folderRejected) && (
                            <S.FolderCurrentDecision $color={primary}>
                                {folderAccepted
                                    ? 'Você aceitou esta proposta. Você pode alterar abaixo.'
                                    : 'Você recusou esta proposta. Você pode alterar abaixo.'}
                            </S.FolderCurrentDecision>
                        )}
                        <S.FolderDecisionRow>
                            <S.OutlineButton
                                $color={primary}
                                $disabled={folderRejected}
                                onClick={folderRejected ? undefined : () => handleFolderDecision('rejected')}
                            >
                                Recusar
                            </S.OutlineButton>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={folderAccepted}
                                onClick={folderAccepted ? undefined : () => handleFolderDecision('accepted')}
                            >
                                Aceitar
                            </S.SubmitButton>
                        </S.FolderDecisionRow>
                    </>
                ) : (
                    <S.SubmitButton
                        $backgroundColor={primary}
                        $disabled={!isChecked}
                        aria-disabled={!isChecked}
                        onClick={handleButtonClick}
                    >
                        {buttonLabel}
                        <ChevronRightIcon width={24} height={24} />
                    </S.SubmitButton>
                )}
                <PoweredByQITech />
            </S.Footer>

            {showCheckboxModal && (
                <CheckboxModalSheet primary={primary} onClose={() => setShowCheckboxModal(false)} />
            )}
        </S.PageWrapper>
    );

    if (hasRefinancing) {
        return renderShell(renderRefinancingVariant(), 'Aceitar e assinar os 2 contratos');
    }

    return renderShell(renderPortabilityVariant(), 'Aceitar proposta');

    function renderPortabilityVariant() {
        return (
            <>
                <S.HeaderBlock>
                    <S.Title $color={primary}>Resumo da portabilidade</S.Title>
                    <S.Subtitle>
                        Revise as condições da portabilidade do seu empréstimo antes de assinar.
                    </S.Subtitle>
                </S.HeaderBlock>

                <S.SectionCaption>Sua dívida hoje</S.SectionCaption>
                <S.DataCardContainer>
                    <S.BankHeader>
                        <S.BankIconTile $color={primary}>
                            <BuildingCityTownIcon size={20} />
                        </S.BankIconTile>
                        <S.BankHeaderText>
                            <S.BankName>{signData?.original_financial_institution}</S.BankName>
                            <S.BankCaption>Instituição de origem da dívida</S.BankCaption>
                        </S.BankHeaderText>
                    </S.BankHeader>
                    <S.DataRow>
                        <S.RowLabel $emphasis>Saldo devedor</S.RowLabel>
                        <S.RowValue $color={primary} $emphasis>
                            ~ {formatCurrency(signData?.due_balance ?? 0)}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Número do contrato</S.RowLabel>
                        <S.RowValue $color={primary}>{signData?.contract_number}</S.RowValue>
                    </S.DataRow>
                </S.DataCardContainer>
                <S.Footnote>
                    ~ Saldo devedor aproximado — o valor exato é confirmado na quitação junto à
                    instituição de origem. As condições finais serão enviadas para o contato informado após a assinatura.
                </S.Footnote>

                <S.SectionCaption>Sua nova operação portada</S.SectionCaption>
                <S.DataCardContainer $accent $color={primary}>
                    <S.CardHeader $color={primary}>
                        <FileTextIcon width={20} height={20} />
                        Proposta de portabilidade
                    </S.CardHeader>
                    <S.DataRow>
                        <S.RowLabel>Valor financiado</S.RowLabel>
                        <S.RowValue $color={primary}>{formatCurrency(creditOperation.issue_amount ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>IOF</S.RowLabel>
                        <S.RowValue $color={primary}>{formatCurrency(creditOperation.iof ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Valor líquido do empréstimo</S.RowLabel>
                        <S.RowValue $color={primary}>{formatCurrency(creditOperation.disbursed_amount ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>CET (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(creditOperation.monthly_cet ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Taxa de juros (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(creditOperation.monthly_interest_rate ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Número de parcelas</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {creditOperation.number_of_installments != null
                                ? `${creditOperation.number_of_installments}`
                                : '-'}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Primeira parcela</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {creditOperation.first_due_date ? formatDate(creditOperation.first_due_date) : '-'}
                        </S.RowValue>
                    </S.DataRow>
                </S.DataCardContainer>

                {renderPortabilityContractCallout()}

            </>
        );
    }

    function renderRefinancingVariant() {
        return (
            <>
                <S.HeaderBlock>
                    <S.Title $color={primary}>Portabilidade com refinanciamento</S.Title>
                    <S.Subtitle>
                        O refinanciamento melhora as condições da dívida que você está portando.
                        Confira as condições e o detalhamento dos contratos a seguir:
                    </S.Subtitle>
                </S.HeaderBlock>

                <S.Merge $color={primary}>
                    <S.MergeMoveRow>
                        <S.MergeNode>
                            <S.MergeNodeLabel>Sua dívida sai de</S.MergeNodeLabel>
                            <S.MergeNodeValue>{signData?.original_financial_institution}</S.MergeNodeValue>
                        </S.MergeNode>
                        <S.MergeArrow $color={primary}>
                            <ArrowRightIcon width={22} height={22} />
                        </S.MergeArrow>
                        <S.MergeNode $align="right">
                            <S.MergeNodeLabel>e passa para</S.MergeNodeLabel>
                            <S.MergeNodeValue>{signData?.signer_company_name}</S.MergeNodeValue>
                        </S.MergeNode>
                    </S.MergeMoveRow>

                    <S.MergeStatsGrid $color={primary}>
                        <S.MergeStatItem>
                            <S.MergeStatLabel>Seu saldo devedor antigo</S.MergeStatLabel>
                            <S.MergeStatValue>{formatCurrency(signData?.due_balance ?? 0)}</S.MergeStatValue>
                        </S.MergeStatItem>
                        <S.MergeStatItem>
                            <S.MergeStatLabel>Seu novo total financiado</S.MergeStatLabel>
                            <S.MergeStatValue>{formatCurrency(refinOp.issue_amount ?? 0)}</S.MergeStatValue>
                        </S.MergeStatItem>
                    </S.MergeStatsGrid>

                    <S.MergeTroco $color={primary}>
                        <S.MergeTrocoLead $color={primary}>
                            <ReceiptIcon size={16} />
                            VOCÊ RECEBE NA CONTA
                        </S.MergeTrocoLead>
                        <S.MergeTrocoValue $color={primary}>
                            {formatCurrency(refinancing.final_disbursed_amount ?? 0)}
                            <sup>*</sup>
                        </S.MergeTrocoValue>
                    </S.MergeTroco>

                    <S.MergeStatsGrid $color={primary}>
                        <S.MergeStatItem>
                            <S.MergeStatLabel>Número de parcelas</S.MergeStatLabel>
                            <S.MergeStatValue>
                                {refinOp.number_of_installments != null
                                    ? `${refinOp.number_of_installments}`
                                    : '-'}
                            </S.MergeStatValue>
                        </S.MergeStatItem>
                        <S.MergeStatItem>
                            <S.MergeStatLabel>Primeira parcela</S.MergeStatLabel>
                            <S.MergeStatValue>
                                {refinOp.first_due_date ? formatDate(refinOp.first_due_date) : '-'}
                            </S.MergeStatValue>
                        </S.MergeStatItem>
                        <S.MergeStatItem>
                            <S.MergeStatLabel>Taxa de juros</S.MergeStatLabel>
                            <S.MergeStatValue>{formatPercent(refinOp.monthly_interest_rate ?? 0)} a.m.</S.MergeStatValue>
                        </S.MergeStatItem>
                        <S.MergeStatItem>
                            <S.MergeStatLabel>CET</S.MergeStatLabel>
                            <S.MergeStatValue>{formatPercent(refinOp.monthly_cet ?? 0)} a.m.</S.MergeStatValue>
                        </S.MergeStatItem>
                        <S.MergeStatItem $full>
                            <S.MergeStatLabel>IOF</S.MergeStatLabel>
                            <S.MergeStatValue>{formatCurrency(refinOp.iof ?? 0)}</S.MergeStatValue>
                        </S.MergeStatItem>
                    </S.MergeStatsGrid>

                    <S.MergeFootnote>
                        * Valor aproximado: depende do saldo real da operação que está sendo portada. As condições finais serão enviadas para o contato informado após a assinatura.
                    </S.MergeFootnote>
                </S.Merge>

                <S.Accordion $color={primary} $open={portabilityOpen}>
                    <S.AccordionHeader
                        $color={primary}
                        type="button"
                        aria-expanded={portabilityOpen}
                        onClick={() => setPortabilityOpen((o) => !o)}
                    >
                        <S.AccordionIconTile $color={primary}>
                            <ChangeSwapArrowIcon size={18} />
                        </S.AccordionIconTile>
                        <S.AccordionTitleBlock>
                            <S.AccordionTitle>Portabilidade</S.AccordionTitle>
                            <S.AccordionSubtitle>Traz a dívida do banco de origem para cá</S.AccordionSubtitle>
                        </S.AccordionTitleBlock>
                        <S.AccordionChevron $color={primary} $open={portabilityOpen}>
                            <ChevronDownIcon width={20} height={20} />
                        </S.AccordionChevron>
                    </S.AccordionHeader>
                    <S.AccordionBody $open={portabilityOpen}>
                        <div className="inner">
                            <S.AccordionInner>
                                <S.InnerCaption>Sua dívida na origem</S.InnerCaption>
                                <S.InnerCardRows>
                                    <S.DataRow>
                                        <S.RowLabel>Banco de origem</S.RowLabel>
                                        <S.RowValue $color={primary}>{signData?.original_financial_institution}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel $emphasis>Saldo devedor</S.RowLabel>
                                        <S.RowValue $color={primary} $emphasis>
                                            ~ {formatCurrency(signData?.due_balance ?? 0)}
                                        </S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>Número do contrato</S.RowLabel>
                                        <S.RowValue $color={primary}>{signData?.contract_number}</S.RowValue>
                                    </S.DataRow>
                                </S.InnerCardRows>

                                <S.InnerCaption>Sua nova operação portada</S.InnerCaption>
                                <S.InnerCardRows>
                                    <S.DataRow>
                                        <S.RowLabel>Valor financiado</S.RowLabel>
                                        <S.RowValue $color={primary}>{formatCurrency(creditOperation.issue_amount ?? 0)}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>CET</S.RowLabel>
                                        <S.RowValue $color={primary}>{formatPercent(creditOperation.monthly_cet ?? 0)}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>Taxa de juros (ao mês)</S.RowLabel>
                                        <S.RowValue $color={primary}>{formatPercent(creditOperation.monthly_interest_rate ?? 0)}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>Número de parcelas</S.RowLabel>
                                        <S.RowValue $color={primary}>
                                            {creditOperation.number_of_installments != null
                                                ? `${creditOperation.number_of_installments}`
                                                : '-'}
                                        </S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>Primeira parcela</S.RowLabel>
                                        <S.RowValue $color={primary}>
                                            {creditOperation.first_due_date ? formatDate(creditOperation.first_due_date) : '-'}
                                        </S.RowValue>
                                    </S.DataRow>
                                </S.InnerCardRows>

                                <S.InnerFootnote>
                                    ~ Saldo devedor aproximado — o valor exato é confirmado na quitação
                                    junto à instituição de origem. As condições finais serão enviadas para o contato informado após a assinatura.
                                </S.InnerFootnote>
                            </S.AccordionInner>
                        </div>
                    </S.AccordionBody>
                </S.Accordion>


                <S.Accordion $color={primary} $open={refinancingOpen}>
                    <S.AccordionHeader
                        $color={primary}
                        type="button"
                        aria-expanded={refinancingOpen}
                        onClick={() => setRefinancingOpen((o) => !o)}
                    >
                        <S.AccordionIconTile $color={primary}>
                            <FileTextIcon width={18} height={18} />
                        </S.AccordionIconTile>
                        <S.AccordionTitleBlock>
                            <S.AccordionTitle>Refinanciamento</S.AccordionTitle>
                            <S.AccordionSubtitle>Refinancia a dívida portada e libera o troco</S.AccordionSubtitle>
                        </S.AccordionTitleBlock>
                        <S.AccordionChevron $color={primary} $open={refinancingOpen}>
                            <ChevronDownIcon width={20} height={20} />
                        </S.AccordionChevron>
                    </S.AccordionHeader>
                    <S.AccordionBody $open={refinancingOpen}>
                        <div className="inner">
                            <S.AccordionInner>
                                <S.InnerCardRows>
                                    <S.DataRow>
                                        <S.RowLabel>Valor total financiado</S.RowLabel>
                                        <S.RowValue $color={primary}>{formatCurrency(refinOp.issue_amount ?? 0)}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel $emphasis>Valor líquido a creditar</S.RowLabel>
                                        <S.RowValue $color={primary} $emphasis>
                                            ~ {formatCurrency(refinancing.final_disbursed_amount ?? 0)}
                                        </S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>IOF</S.RowLabel>
                                        <S.RowValue $color={primary}>{formatCurrency(refinOp.iof ?? 0)}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>CET</S.RowLabel>
                                        <S.RowValue $color={primary}>{formatPercent(refinOp.monthly_cet ?? 0)}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>Taxa de juros (ao mês)</S.RowLabel>
                                        <S.RowValue $color={primary}>{formatPercent(refinOp.monthly_interest_rate ?? 0)}</S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>Número de parcelas</S.RowLabel>
                                        <S.RowValue $color={primary}>
                                            {refinOp.number_of_installments != null
                                                ? `${refinOp.number_of_installments}`
                                                : '-'}
                                        </S.RowValue>
                                    </S.DataRow>
                                    <S.DataRow>
                                        <S.RowLabel>Primeira parcela</S.RowLabel>
                                        <S.RowValue $color={primary}>
                                            {refinOp.first_due_date ? formatDate(refinOp.first_due_date) : '-'}
                                        </S.RowValue>
                                    </S.DataRow>
                                </S.InnerCardRows>

                                <S.InnerFootnote>
                                    ~ O valor líquido a creditar é aproximado, pois depende do saldo real
                                    da operação que está sendo portada. As condições finais serão enviadas para o contato informado após a assinatura.
                                </S.InnerFootnote>
                            </S.AccordionInner>
                        </div>
                    </S.AccordionBody>
                </S.Accordion>

                {renderPortabilityContractCallout()}

                {renderRefinancingContractCallout()}

            </>
        );
    }
};

const CheckboxModalSheet = ({ primary, onClose }: { primary: string; onClose: () => void }) => (
    <S.ModalOverlay onClick={onClose}>
        <S.ModalSheet onClick={(e) => e.stopPropagation()}>
            <S.SheetHandle />
            <S.ModalTitle $color={primary}>Confirmação pendente</S.ModalTitle>
            <S.ModalText style={{ textAlign: 'start' }}>
                Para continuar, você precisa marcar a seguinte caixa acima:
                <ul>
                    <li>
                        <strong>Aceite das condições de contratação</strong>
                    </li>
                </ul>
            </S.ModalText>
            <S.ModalButtonRow>
                <S.SubmitButton $backgroundColor={primary} $disabled={false} onClick={onClose}>
                    Entendi
                    <ChevronRightIcon width={24} height={24} />
                </S.SubmitButton>
            </S.ModalButtonRow>
        </S.ModalSheet>
    </S.ModalOverlay>
);
